import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import ScheduleIcon from '@mui/icons-material/Schedule';

const CreditExpiryModal = ({ isOpen, onClose, expiryData }) => {
    const { t, i18n } = useTranslation();

    const formatDate = (dateString) => {
        if (!dateString) return t('dashboard.notAvailable');
        const locale = i18n.language === 'hi' ? 'hi-IN' : i18n.language || 'en-US';
        const date = new Date(dateString);
        return date.toLocaleDateString(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getExpiryStatusColor = (daysUntilExpiry) => {
        if (daysUntilExpiry <= 7) return 'text-red-600';
        if (daysUntilExpiry <= 30) return 'text-orange-600';
        return 'text-green-600';
    };

    const getExpiryStatusText = (daysUntilExpiry) => {
        if (daysUntilExpiry <= 7) return t('dashboard.critical');
        if (daysUntilExpiry <= 30) return t('dashboard.warning');
        return t('dashboard.safe');
    };

    const getDaysUntilExpiry = (expiryDate) => {
        if (!expiryDate) return 0;
        const now = new Date();
        const expiry = new Date(expiryDate);
        const diffTime = expiry - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const activeExpiriesList = (expiryData.contributing_buckets && expiryData.contributing_buckets.length > 0)
        ? expiryData.contributing_buckets
        : (expiryData.upcoming_expiries || []);

    const footer = (
        <div className="flex justify-end">
            <button
                onClick={onClose}
                className="px-6 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
            >
                {t('common.cancel')}
            </button>
        </div>
    );

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[10000] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div
                className="bg-white rounded-3xl md:w-[60%] w-[95%] max-h-[95vh] flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white rounded-t-3xl">
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">{t('dashboard.creditExpiryDetails')}</h2>
                        <p className="text-sm text-gray-600 mt-1">{t('dashboard.creditExpiryDetailsDescription')}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-900 transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto bg-gray-50/50 p-6">
                    {/* Summary Section */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-4 border border-blue-200/50">
                            <div className="text-sm font-medium text-blue-800 mb-1">{t('dashboard.latestExpiryShort')}</div>
                            <div className="text-lg font-bold text-blue-900">
                                {(expiryData.nearest_expiry_date || expiryData.latest_expiry_date)
                                    ? formatDate(expiryData.nearest_expiry_date || expiryData.latest_expiry_date)
                                    : t('dashboard.notAvailable')}
                            </div>
                        </div>
                        <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-xl p-4 border border-orange-200/50">
                            <div className="text-sm font-medium text-orange-800 mb-1">{t('dashboard.totalExpiring')}</div>
                            <div className="text-lg font-bold text-orange-900">
                                {expiryData.total_active_expiring ?? expiryData.total_expiring_credits}
                            </div>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl p-4 border border-green-200/50">
                            <div className="text-sm font-medium text-green-800 mb-1">{t('dashboard.currentTime')}</div>
                            <div className="text-lg font-bold text-green-900">
                                {expiryData.current_time ? formatDate(expiryData.current_time) : t('dashboard.notAvailable')}
                            </div>
                        </div>
                    </div>

                    {/* Upcoming Expiries Section */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <ScheduleIcon className="text-orange-600" />
                            {t('dashboard.topUpcomingExpiries')}
                        </h3>
                        {activeExpiriesList && activeExpiriesList.length > 0 ? (
                            <div className="bg-white rounded-xl overflow-hidden border border-gray-200/50 shadow-sm">
                                {/* Header - Hidden on mobile */}
                                <div className="hidden md:grid md:grid-cols-12 gap-4 p-4 text-sm font-medium text-gray-700 border-b border-gray-200 bg-gray-50">
                                    <div className="col-span-2">{t('dashboard.amount')}</div>
                                    <div className="col-span-3">{t('dashboard.expiryDate')}</div>
                                    <div className="col-span-2">{t('dashboard.daysLeft')}</div>
                                    <div className="col-span-3">{t('dashboard.companyAccount')}</div>
                                    <div className="col-span-2">{t('dashboard.status')}</div>
                                </div>
                                {activeExpiriesList.slice(0, 5).map((expiry, index) => {
                                    const daysLeft = expiry.days_until_expiry !== undefined ? expiry.days_until_expiry : getDaysUntilExpiry(expiry.expiry_date);
                                    return (
                                        <div key={index} className="flex flex-col md:grid md:grid-cols-12 gap-2 md:gap-4 p-4 border-b border-gray-100 hover:bg-blue-50/30 transition-colors">
                                            <div className="flex justify-between md:block md:col-span-2">
                                                <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.amount')}</span>
                                                <div className="text-sm font-medium text-gray-900">{t('dashboard.creditsCount', { count: expiry.remaining_balance ?? expiry.remaining_amount ?? expiry.amount })}</div>
                                            </div>
                                            <div className="flex justify-between md:block md:col-span-3">
                                                <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.expiryDate')}</span>
                                                <div className="text-sm text-gray-700">{formatDate(expiry.expiry_date)}</div>
                                            </div>
                                            <div className="flex justify-between md:block md:col-span-2">
                                                <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.daysLeft')}</span>
                                                <span className={`text-sm font-semibold ${getExpiryStatusColor(daysLeft)}`}>
                                                    {t('dashboard.daysLeftCount', { count: daysLeft })}
                                                </span>
                                            </div>
                                            <div className="flex justify-between md:block md:col-span-3">
                                                <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.companyAccount')}</span>
                                                <div className="text-sm text-gray-700">
                                                    {expiry.company_name || expiry.credit_account_owner || t('dashboard.notAvailable')}
                                                </div>
                                            </div>
                                            <div className="flex justify-between md:block md:col-span-2">
                                                <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.status')}</span>
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${daysLeft <= 7
                                                    ? 'bg-red-100 text-red-800'
                                                    : daysLeft <= 30
                                                        ? 'bg-orange-100 text-orange-800'
                                                        : 'bg-green-100 text-green-800'
                                                    }`}>
                                                    {getExpiryStatusText(daysLeft)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl border border-gray-200/50 py-12 text-center text-gray-500">
                                <ScheduleIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                <p className="font-medium">{t('dashboard.noUpcomingExpiries')}</p>
                            </div>
                        )}
                    </div>

                    {/* All Upcoming Expiries Section */}
                    {activeExpiriesList && activeExpiriesList.length > 5 && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">All Upcoming Expiries</h3>
                            <div className="bg-white rounded-xl overflow-hidden border border-gray-200/50 shadow-sm">
                                {/* Header - Hidden on mobile */}
                                <div className="hidden md:grid md:grid-cols-12 gap-4 p-4 text-sm font-medium text-gray-700 border-b border-gray-200 bg-gray-50">
                                    <div className="col-span-2">{t('dashboard.amount')}</div>
                                    <div className="col-span-3">{t('dashboard.expiryDate')}</div>
                                    <div className="col-span-2">{t('dashboard.daysLeft')}</div>
                                    <div className="col-span-3">{t('dashboard.companyAccount')}</div>
                                    <div className="col-span-2">{t('dashboard.notes')}</div>
                                </div>
                                {activeExpiriesList.map((expiry, index) => {
                                    const daysLeft = expiry.days_until_expiry !== undefined ? expiry.days_until_expiry : getDaysUntilExpiry(expiry.expiry_date);
                                    return (
                                        <div key={index} className="flex flex-col md:grid md:grid-cols-12 gap-2 md:gap-4 p-4 border-b border-gray-100 hover:bg-blue-50/30 transition-colors">
                                            <div className="flex justify-between md:block md:col-span-2">
                                                <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.amount')}</span>
                                                <div className="text-sm font-medium text-gray-900">{t('dashboard.creditsCount', { count: expiry.remaining_balance ?? expiry.remaining_amount ?? expiry.amount })}</div>
                                            </div>
                                            <div className="flex justify-between md:block md:col-span-3">
                                                <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.expiryDate')}</span>
                                                <div className="text-sm text-gray-700">{formatDate(expiry.expiry_date)}</div>
                                            </div>
                                            <div className="flex justify-between md:block md:col-span-2">
                                                <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.daysLeft')}</span>
                                                <span className={`text-sm font-semibold ${getExpiryStatusColor(daysLeft)}`}>
                                                    {t('dashboard.daysLeftCount', { count: daysLeft })}
                                                </span>
                                            </div>
                                            <div className="flex justify-between md:block md:col-span-3">
                                                <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.companyAccount')}</span>
                                                <div className="text-sm text-gray-700">
                                                    {expiry.company_name || expiry.credit_account_owner || t('dashboard.notAvailable')}
                                                </div>
                                            </div>
                                            <div className="flex justify-between md:block md:col-span-2">
                                                <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.notes')}</span>
                                                <div className="text-sm text-gray-600">
                                                    {expiry.notes || '-'}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Recently Expired Credits Section */}
                    {expiryData.expired_credits && expiryData.expired_credits.length > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <ScheduleIcon className="text-red-600" />
                                {t('dashboard.recentlyExpired')}
                            </h3>
                            <div className="bg-white rounded-xl overflow-hidden border border-gray-200/50 shadow-sm">
                                {/* Header - Hidden on mobile */}
                                <div className="hidden md:grid md:grid-cols-12 gap-4 p-4 text-sm font-medium text-gray-700 border-b border-gray-200 bg-gray-50">
                                    <div className="col-span-2">{t('dashboard.amount')}</div>
                                    <div className="col-span-3">{t('dashboard.expiryDate')}</div>
                                    <div className="col-span-2">{t('dashboard.daysAgoLabel')}</div>
                                    <div className="col-span-3">{t('dashboard.companyAccount')}</div>
                                    <div className="col-span-2">{t('dashboard.notes')}</div>
                                </div>
                                {expiryData.expired_credits.map((expiry, index) => (
                                    <div key={index} className="flex flex-col md:grid md:grid-cols-12 gap-2 md:gap-4 p-4 border-b border-gray-100 hover:bg-red-50/30 transition-colors">
                                        <div className="flex justify-between md:block md:col-span-2">
                                            <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.amount')}</span>
                                            <div className="text-sm font-medium text-gray-900">{t('dashboard.creditsCount', { count: expiry.amount })}</div>
                                        </div>
                                        <div className="flex justify-between md:block md:col-span-3">
                                            <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.expiryDate')}</span>
                                            <div className="text-sm text-gray-700">{formatDate(expiry.expiry_date)}</div>
                                        </div>
                                        <div className="flex justify-between md:block md:col-span-2">
                                            <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.daysAgoLabel')}</span>
                                            <span className="text-sm font-semibold text-red-600">
                                                {t('dashboard.daysAgoCount', { count: expiry.days_since_expiry })}
                                            </span>
                                        </div>
                                        <div className="flex justify-between md:block md:col-span-3">
                                            <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.companyAccount')}</span>
                                            <div className="text-sm text-gray-700">
                                                {expiry.company_name || expiry.credit_account_owner || t('dashboard.notAvailable')}
                                            </div>
                                        </div>
                                        <div className="flex justify-between md:block md:col-span-2">
                                            <span className="md:hidden text-xs font-semibold text-gray-500 uppercase">{t('dashboard.notes')}</span>
                                            <div className="text-sm text-gray-600">
                                                {expiry.notes || '-'}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200 bg-white rounded-b-3xl">
                    {footer}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CreditExpiryModal;
